/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from "react";
import logo from "../../assets/images/pebble-logo.png";
import illustrator from "../../assets/images/illustration_container.svg";
import google_icon from "../../assets/images/google_icon.svg";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import PopupModalCreate from "../CreateAccount/PopUpModalCreate";
import illustratorMPS from "../../assets/images/illustrator_mps.svg";
import { handleForgotPassword } from "../../firebaseFunctions/forgotPassword/forgotPassword";
import { Eye, EyeOff } from "lucide-react";
import { sendEmailVerification } from "firebase/auth";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import { useTranslation } from "react-i18next";
import LegalModal from "../../components/ui/LegalModal";
import DarkModeToggle from "../../components/ui/DarkModeToggle";

const Login = () => {
  const { t } = useTranslation();
  const { user, signIn, signInWithGoogle, signOut, initialAuthCheckDone, hasActiveSubscription, isSignedUp, userType: authUserType } = useAuth();
  const location = useLocation();
  const [selectedUserType, setSelectedUserType] = useState(() => {
    return location.state?.userType || "EO";
  });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotEmailSent, setForgotEmailSent] = useState(false);
  const [eventTypeIndex, setEventTypeIndex] = useState(0);

  const eventTypeKeys = [
    "festival", "farmersMarket", "fleaMarket", "artisanMarket", "popUp",
    "vendorFair", "marketplace", "shoppingSquare", "concertConcessions",
    "sportingEventConcessions", "artFair", "merchStands"
  ];

  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showVerificationAlert, setShowVerificationAlert] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [legalModal, setLegalModal] = useState({ isOpen: false, type: null });

  // Ref to track when login is in progress - prevents useEffect auto-redirect from racing with handleSubmit/handleGoogleSignIn navigation
  const loginInProgressRef = useRef(false);

  const handleOpen = () => setOpenConfirmation(true);
  const handleClose = () => setOpenConfirmation(false);

  const isEmailValid = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const navigate = useNavigate();

  useEffect(() => {
    // Skip redirect during active login process - handleSubmit/handleGoogleSignIn handle navigation
    if (loginInProgressRef.current) return;

    // Only redirect if Firebase has finished checking auth state
    if (initialAuthCheckDone && user && authUserType) {
      // Redirect based on user type and subscription status
      if (authUserType === "EO") {
        if (isSignedUp && hasActiveSubscription) {
          navigate('/eo/dashboard');
        } else {
          navigate('/eo-paywall');
        }
      } else if (authUserType === "MPS") {
        navigate('/mps/dashboard');
      } else {
        navigate('/');
      }
    }
  }, [user, initialAuthCheckDone, authUserType, isSignedUp, hasActiveSubscription, navigate]);

  useEffect(() => {
    const interval = setInterval(() => {
      setEventTypeIndex((prev) => (prev + 1) % eventTypeKeys.length);
    }, 3500);
    return () => clearInterval(interval);
  }, [eventTypeKeys.length]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    loginInProgressRef.current = true;
    try {
      const result = await signIn(email, password, selectedUserType);
      if (!result.success) {
        alert(result.message);
      } else {
        if (!result.user.emailVerified) {
          setIsLoading(false);
          setShowVerificationAlert(true);
          await sendEmailVerification(result.user, {
            url: window.location.origin + '/login',
            handleCodeInApp: true,
          });
          await signOut();
          return;
        }
        // Mark this as a returning user login for welcome message
        sessionStorage.setItem('showWelcomeBack', 'true');
        setIsLoading(false);
        setIsTransitioning(true);
        await new Promise(r => setTimeout(r, 900));

        if (selectedUserType === "EO") {
          if (result.isSignedUp) {
            navigate("/eo/dashboard");
          } else {
            navigate("/eo-paywall");
          }
        } else {
          navigate("/mps/dashboard");
        }
      }
    } catch (error) {
      console.error("Error signing in:", error);
      alert(error.message);
    }
    setIsLoading(false);
  };

  const forgotPassword = async (email) => {
    try {
      setIsLoading(true);
      if (isEmailValid(email)) {
        const result = await handleForgotPassword(email);
        if (result?.message) {
          setForgotEmailSent(true);
        } else {
          alert("Something went wrong. Please try again.");
        }
      } else {
        alert("Please enter a valid email.");
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Error in forgotPassword:", error);
      alert("An error occurred. Please try again later.");
    }
  };

  const handleGoogleSignIn = async (loginUserType) => {
    setIsLoading(true);
    loginInProgressRef.current = true;
    try {
      const response = await signInWithGoogle(loginUserType);
      if(response.success) {
        setIsLoading(false);
        if (response.isSignedUp) {
          sessionStorage.setItem('showWelcomeBack', 'true');
          setIsTransitioning(true);
          await new Promise(r => setTimeout(r, 900));
          const dashboardPath = loginUserType === "EO" ? "/eo/dashboard" : "/mps/dashboard";
          navigate(dashboardPath);
        } else {
          if (loginUserType === "EO") {
            navigate('/eo-paywall');
          } else {
            navigate('/mps-signup');
          }
        }
      } else {
        setIsLoading(false);
        alert(response.message || "Failed to sign in with Google");
      }
    } catch (error) {
      setIsLoading(false);
      alert("Error: " + (error.message || error.toString()));
    }
  };

  const VerificationAlert = () => {
    if (!showVerificationAlert) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-gray-900 opacity-50"></div>
        <div className="relative bg-white rounded-xl shadow-lg p-6 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-10 w-10 rounded-full bg-yellow-100 mb-3">
              <svg className="h-5 w-5 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-base font-medium text-gray-900 mb-2">Email not verified</h3>
            <p className="text-sm text-gray-500 mb-4">
              Please verify your email. We've sent a new link to {email}.
            </p>
            <button
              onClick={() => setShowVerificationAlert(false)}
              className="w-full py-2 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (isTransitioning) {
    return (
      <div className="fixed inset-0 bg-grey-50 z-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <img className="h-10 w-auto" src={logo} alt="Pebble" />
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-pebble-red rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
            <div className="w-2 h-2 bg-pebble-red rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
            <div className="w-2 h-2 bg-pebble-red rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
          <p className="text-sm text-grey-500">Taking you to your dashboard…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-grey-50 flex flex-col items-center justify-start md:justify-center p-4 pt-16 md:pt-4 overflow-x-hidden">
      {isLoading && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white/80 backdrop-blur-sm">
          <div className="w-10 h-10 rounded-full border-4 border-gray-200 border-t-gray-900 animate-spin" />
        </div>
      )}
      <VerificationAlert />
      
      <div className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-4 py-2 bg-grey-50/95 backdrop-blur-sm md:bg-transparent md:backdrop-blur-none md:absolute md:top-3 md:right-3 md:left-auto md:justify-end md:gap-2 md:p-0">
        <Link to="/" className="md:hidden">
          <img className="h-6 w-auto" src={logo} alt="Pebble" />
        </Link>
        <div className="flex items-center gap-2">
          <DarkModeToggle variant="icon" />
          <LanguageSwitcher />
          <Link
            to="/browse-events"
            className="px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-pebble-red shadow-md hover:bg-pebble-red/90 hover:shadow-lg transition-all duration-150 active:scale-[0.98] active:shadow-sm cursor-pointer"
          >
            {t('navigation.browseEvents')}
          </Link>
        </div>
      </div>

      <Link to="/" className="hidden md:block">
        <img className="h-8 w-auto mb-4" src={logo} alt="Pebble" />
      </Link>

      <div className="bg-white shadow-lg rounded-2xl flex flex-col md:flex-row w-full max-w-[880px]">
        <div className="w-full md:w-1/2 p-6">
          <h2 className="text-base font-medium mb-0.5 text-center text-grey-600">
            {t('auth.login.title')}
          </h2>
          <div className="text-lg font-semibold mb-4 text-center">
            <span className="text-grey-900">{(() => {
              const eventTypeKey = eventTypeKeys[eventTypeIndex];
              const eventType = t(`eventTypes.${eventTypeKey}`);
              const pluralTypes = ['concertConcessions', 'sportingEventConcessions', 'merchStands'];
              if (pluralTypes.includes(eventTypeKey)) return t('auth.signup.manage');
              return /^[aeiouAEIOU]/.test(eventType) ? t('auth.signup.manageAn') : t('auth.signup.manageA');
            })()} </span>
            <span key={eventTypeIndex} className="text-pebble-red animate-fade-in">
              {t(`eventTypes.${eventTypeKeys[eventTypeIndex]}`)}
            </span>.
          </div>

          <div className="flex rounded-lg shadow-md mb-4 border border-grey-300 overflow-hidden">
            <button
              type="button"
              className={`${selectedUserType === "EO" ? "bg-primary-400 text-white shadow-inner" : "bg-white text-grey-900 hover:bg-grey-50"} w-1/2 py-2 text-sm font-medium rounded-l-lg cursor-pointer transition-all duration-150 active:scale-[0.98]`}
              onClick={() => setSelectedUserType("EO")}
            >
              {t('auth.userTypes.eventOrganizer')}
            </button>
            <button
              type="button"
              className={`${selectedUserType === "MPS" ? "bg-primary-400 text-white shadow-inner" : "bg-white text-grey-900 hover:bg-grey-50"} w-1/2 py-2 text-sm font-medium rounded-r-lg cursor-pointer transition-all duration-150 active:scale-[0.98]`}
              onClick={() => setSelectedUserType("MPS")}
            >
              {t('auth.userTypes.marketplaceSeller')}
            </button>
          </div>

          <button
            type="button"
            className="w-full h-10 flex justify-center items-center border border-grey-300 rounded-lg shadow-md bg-white text-sm font-semibold hover:bg-grey-50 hover:shadow-lg mb-4 cursor-pointer transition-all duration-150 active:scale-[0.98] active:shadow-sm"
            onClick={() => handleGoogleSignIn(selectedUserType)}
          >
            <img className="h-4 w-4 mr-2" src={google_icon} alt="Google" />
            {t('auth.login.signInWithGoogle')}
          </button>

          <div className="relative mb-4 flex items-center">
            <div className="flex-grow border-t border-gray-300"></div>
            <span className="px-3 text-xs text-gray-500">{t('auth.login.orSignInWithEmail')}</span>
            <div className="flex-grow border-t border-gray-300"></div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="block text-xs font-medium text-grey-700 mb-1">
                {t('auth.login.emailLabel')}
              </label>
              <input
                type="email"
                required
                className="w-full h-10 px-3 border border-grey-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-pebble-red"
                placeholder={t('auth.login.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="mb-2">
              <label className="block text-xs font-medium text-grey-700 mb-1">
                {t('auth.login.passwordLabel')}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  className="w-full h-10 px-3 pr-10 border border-grey-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-pebble-red"
                  placeholder={t('auth.login.passwordPlaceholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-grey-400"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p
                className="text-xs text-grey-600 mt-1 text-right cursor-pointer hover:text-pebble-red"
                onClick={() => setOpenConfirmation(true)}
              >
                {t('auth.login.forgotPassword')}
              </p>
            </div>

            <div className="flex items-center mb-4">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 border-grey-300 rounded accent-pebble-red"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
              />
              <label className="ml-2 text-xs text-grey-700">{t('auth.login.rememberMe')}</label>
            </div>

            <button
              type="submit"
              disabled={!email || !password || isLoading}
              className={`w-full h-10 rounded-lg text-sm font-semibold transition-all duration-150 ${
                email && password && !isLoading
                  ? "bg-grey-700 text-white hover:bg-grey-800 shadow-md hover:shadow-lg active:scale-[0.98] active:shadow-sm cursor-pointer"
                  : "bg-grey-300 text-grey-500 cursor-not-allowed"
              }`}
            >
              {isLoading ? t('common.loading') : t('common.continue')}
            </button>
          </form>

          <p className="mt-3 text-center text-xs text-gray-600">
            {t('auth.login.noAccount')}{' '}
            <span className="text-pebble-red cursor-pointer hover:underline" onClick={() => navigate('/signup', { state: { userType: selectedUserType } })}>
              {t('auth.login.signUpLink')}
            </span>
          </p>

          <p className="mt-3 text-center text-[10px] text-gray-400 transition-colors duration-300">
            {t('footer.termsAgreement')}{' '}
            <button 
              type="button"
              onClick={() => setLegalModal({ isOpen: true, type: 'terms' })}
              className="underline hover:text-pebble-red transition-colors"
            >
              {t('footer.terms')}
            </button>
            {' '}{t('footer.and')}{' '}
            <button 
              type="button"
              onClick={() => setLegalModal({ isOpen: true, type: 'privacy' })}
              className="underline hover:text-pebble-red transition-colors"
            >
              {t('footer.privacyPolicy')}
            </button>
          </p>
        </div>

        <div className="hidden md:block w-1/2 bg-primary-primary-red rounded-r-2xl overflow-hidden">
          <img
            className="object-cover w-full h-full"
            src={selectedUserType === "EO" ? illustrator : illustratorMPS}
            alt="Illustration"
          />
        </div>
      </div>

      <div className="mt-3 text-center transition-colors duration-300">
        <nav className="flex justify-center items-center gap-x-1.5 text-[9px] text-grey-500 dark:text-grey-400 mb-1.5 whitespace-nowrap transition-colors duration-300">
          <Link to="/about" className="hover:text-pebble-red transition-colors duration-300">{t('footer.about')}</Link>
          <span className="dark:text-grey-500 transition-colors duration-300">·</span>
          <Link to="/careers" className="hover:text-pebble-red transition-colors duration-300">{t('footer.careers')}</Link>
          <span className="dark:text-grey-500 transition-colors duration-300">·</span>
          <Link to="/browse-events" className="hover:text-pebble-red transition-colors duration-300">{t('footer.directory')}</Link>
          <span className="dark:text-grey-500 transition-colors duration-300">·</span>
          <Link to="/partnerships" className="hover:text-pebble-red transition-colors duration-300">{t('footer.partnerships')}</Link>
          <span className="dark:text-grey-500 transition-colors duration-300">·</span>
          <Link to="/faqs" className="hover:text-pebble-red transition-colors duration-300">{t('footer.faqs')}</Link>
          <span className="dark:text-grey-500 transition-colors duration-300">·</span>
          <Link to="/host" className="hover:text-pebble-red transition-colors duration-300">{t('footer.host')}</Link>
          <span className="dark:text-grey-500 transition-colors duration-300">·</span>
          <Link to="/contact" className="hover:text-pebble-red transition-colors duration-300">{t('footer.contact')}</Link>
          <span className="dark:text-grey-500 transition-colors duration-300">·</span>
          <Link to="/invest" className="hover:text-pebble-red transition-colors duration-300">{t('footer.invest')}</Link>
          <span className="dark:text-grey-500 transition-colors duration-300">·</span>
          <Link to="/support" className="hover:text-pebble-red transition-colors duration-300">{t('footer.support')}</Link>
        </nav>
        <div className="flex justify-center space-x-3 mb-1">
          <a href="https://www.facebook.com/pebbletheapp" target="_blank" rel="noopener noreferrer" className="text-grey-400 dark:text-grey-500 hover:text-pebble-red transition-colors duration-300">
            <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
          </a>
          <a href="https://www.instagram.com/pebbletheappofficial?igsh=MTB5cHk4aGJtdm54Zw==" target="_blank" rel="noopener noreferrer" className="text-grey-400 dark:text-grey-500 hover:text-pebble-red transition-colors duration-300">
            <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 24 24"><path d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"/></svg>
          </a>
          <a href="https://www.linkedin.com/company/pebbletheapp" target="_blank" rel="noopener noreferrer" className="text-grey-400 dark:text-grey-500 hover:text-pebble-red transition-colors duration-300">
            <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
          </a>
          <a href="https://youtube.com/@techloungepodcast?si=FDYcAQBFKd7xg-BB" target="_blank" rel="noopener noreferrer" className="text-grey-400 dark:text-grey-500 hover:text-pebble-red transition-colors duration-300">
            <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
          </a>
          <a href="https://twitter.com/pebbletheapp" target="_blank" rel="noopener noreferrer" className="text-grey-400 dark:text-grey-500 hover:text-pebble-red transition-colors duration-300">
            <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
          </a>
        </div>
        <p className="text-[8px] text-grey-400 dark:text-grey-500 mt-4 transition-colors duration-300">
          {t('footer.copyright')}
        </p>
      </div>

      <PopupModalCreate isOpen={openConfirmation} onClose={handleClose} handleSubmit={setShowForgotPassword}>
        <div className="bg-white rounded-xl p-6 max-w-sm w-full">
          {isLoading ? (
            <div className="flex flex-col items-center py-4">
              <p className="text-sm text-grey-600">Processing...</p>
            </div>
          ) : forgotEmailSent ? (
            <div className="text-center">
              <h3 className="text-base font-semibold mb-2">Check your email</h3>
              <p className="text-sm text-grey-600 mb-4">Password reset link sent.</p>
              <button
                className="w-full py-2 bg-grey-700 text-white rounded-lg text-sm font-medium"
                onClick={() => { setForgotEmailSent(false); handleClose(); }}
              >
                Continue
              </button>
            </div>
          ) : (
            <div>
              <h3 className="text-base font-semibold mb-1 text-center">Forgot password</h3>
              <p className="text-xs text-grey-600 mb-4 text-center">Enter your email for a reset link.</p>
              <input
                type="email"
                className="w-full h-10 px-3 border border-grey-300 rounded-lg text-sm mb-4"
                placeholder="Enter your email"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
              />
              <div className="flex gap-3">
                <button
                  className="flex-1 py-2 border border-grey-300 rounded-lg text-sm font-medium"
                  onClick={handleClose}
                >
                  Cancel
                </button>
                <button
                  className="flex-1 py-2 bg-grey-700 text-white rounded-lg text-sm font-medium"
                  onClick={() => forgotPassword(forgotPasswordEmail)}
                >
                  Reset
                </button>
              </div>
            </div>
          )}
        </div>
      </PopupModalCreate>

      <LegalModal 
        isOpen={legalModal.isOpen}
        onClose={() => setLegalModal({ isOpen: false, type: null })}
        type={legalModal.type}
      />
    </div>
  );
};

export default Login;
